import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { eq } from 'tio-ui/utilities';
import { dropTask } from 'ember-concurrency';
import Text from 'tio-reimagined/components/text';
import {
  PhWarningCircle,
  PhCaretDown,
  PhCaretUp,
  PhXCircle,
  PhCheckCircle,
} from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';

interface AddressOption {
  id: string;
  label: string;
}

interface AddressSelectionCardSignature {
  Args: {
    /** Header title for the issue */
    title: string;
    /** Description shown below the title */
    description: string;
    /** Available addresses for selection */
    addresses: AddressOption[];
    /** Called when user submits an address selection; receives the selected address id */
    onSubmit: (addressId: string) => Promise<void>;
  };
  Element: HTMLDivElement;
}

export default class AddressSelectionCard extends Component<AddressSelectionCardSignature> {
  @service declare intl: IntlService;

  @tracked isExpanded = false;
  @tracked selectedAddressId: string | null = null;
  @tracked isDropdownOpen = false;
  @tracked isSuccess = false;
  @tracked hasError = false;

  get selectedAddress(): AddressOption | undefined {
    return this.args.addresses.find((a) => a.id === this.selectedAddressId);
  }

  get isSubmitDisabled(): boolean {
    return !this.selectedAddressId || this.handleSubmit.isRunning;
  }

  toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  toggleDropdown = () => {
    this.isDropdownOpen = !this.isDropdownOpen;
  };

  selectAddress = (address: AddressOption) => {
    this.selectedAddressId = address.id;
    this.hasError = false;
    this.isDropdownOpen = false;
  };

  clearSelection = () => {
    this.selectedAddressId = null;
    this.hasError = false;
  };

  handleSubmit = dropTask(async (e: Event) => {
    e.preventDefault();
    if (!this.selectedAddressId) return;

    this.hasError = false;

    try {
      await this.args.onSubmit(this.selectedAddressId);
      this.isSuccess = true;
    } catch {
      this.hasError = true;
    }
  });

  <template>
    <div
      class="bg-white border border-gray-200 flex flex-col gap-6 items-start px-4 py-6 rounded-2xl"
      data-test-address-selection-card
      ...attributes
    >
      {{#if this.isSuccess}}
        {{! ── Success State (Figma: Blank Payment Address / Success) ── }}
        <div class="flex items-center gap-3 w-full" data-test-address-selection-success>
          <div
            class="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 shrink-0"
          >
            <PhCheckCircle @size={{20}} class="text-emerald-600" @weight="fill" />
          </div>
          <Text class="text-base font-medium text-gray-700">
            {{t "slr.reimagined.address_selection.success_message"}}
          </Text>
        </div>
      {{else}}
        {{! ── Header: Icon + Title/Description + Caret Toggle ── }}
        <button
          type="button"
          class="flex items-center justify-between w-full cursor-pointer"
          data-test-address-selection-toggle
          aria-expanded="{{this.isExpanded}}"
          {{on "click" this.toggleExpanded}}
        >
          <div class="flex gap-3 items-center">
            <div class="flex items-center justify-center rounded-lg shrink-0 size-12 bg-red-100">
              <PhWarningCircle @size={{32}} class="text-red-600" />
            </div>
            <div class="flex flex-col gap-1 items-start text-left">
              <Text
                class="font-semibold text-base text-gray-700 capitalize tracking-tight leading-snug"
              >
                {{@title}}
              </Text>
              <Text class="font-medium text-base text-gray-500 leading-snug">
                {{@description}}
              </Text>
            </div>
          </div>
          <div class="flex items-center justify-center shrink-0 ml-4">
            {{#if this.isExpanded}}
              <PhCaretUp @size={{24}} class="text-gray-700" />
            {{else}}
              <PhCaretDown @size={{24}} class="text-gray-700" />
            {{/if}}
          </div>
        </button>

        {{! ── Expanded Content Area ── }}
        {{#if this.isExpanded}}
          <div
            class="bg-sky-50 flex flex-col items-start p-4 rounded-2xl w-full gap-4"
            data-test-address-selection-expanded
          >
            <Text class="text-base text-gray-700 leading-relaxed">
              {{t "slr.reimagined.address_selection.instruction"}}
            </Text>

            {{! ── Dropdown Container ── }}
            <div class="relative w-full max-w-md">
              {{#if this.selectedAddress}}
                {{! ── Selected state: show address with clear button ── }}
                <div
                  class="flex items-center justify-between gap-2 w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700"
                  data-test-address-selection-selected
                >
                  <span class="truncate">{{this.selectedAddress.label}}</span>
                  <button
                    type="button"
                    class="shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer"
                    aria-label={{t "slr.reimagined.address_selection.clear_selection"}}
                    data-test-address-selection-clear
                    {{on "click" this.clearSelection}}
                  >
                    <PhXCircle @size={{18}} />
                  </button>
                </div>
              {{else}}
                {{! ── Unselected state: clickable dropdown trigger ── }}
                <button
                  type="button"
                  class="flex items-center justify-between w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 text-sm cursor-pointer hover:border-gray-400"
                  data-test-address-selection-trigger
                  aria-haspopup="listbox"
                  aria-expanded="{{this.isDropdownOpen}}"
                  {{on "click" this.toggleDropdown}}
                >
                  <span class="text-gray-400">
                    {{t "slr.reimagined.address_selection.placeholder"}}
                  </span>
                  {{#if this.isDropdownOpen}}
                    <PhCaretUp @size={{16}} class="text-gray-500" />
                  {{else}}
                    <PhCaretDown @size={{16}} class="text-gray-500" />
                  {{/if}}
                </button>
              {{/if}}

              {{! ── Dropdown options ── }}
              {{#if this.isDropdownOpen}}
                <ul
                  class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
                  role="listbox"
                  data-test-address-selection-options
                >
                  {{#each @addresses as |address|}}
                    <li
                      class="w-full text-left px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      role="option"
                      aria-selected="{{if (eq this.selectedAddressId address.id) 'true' 'false'}}"
                      data-test-address-option={{address.id}}
                      {{on "click" (fn this.selectAddress address)}}
                    >
                      {{address.label}}
                    </li>
                  {{/each}}
                </ul>
              {{/if}}
            </div>

            {{! ── Error message ── }}
            {{#if this.hasError}}
              <Text class="text-sm text-red-600" data-test-address-selection-error>
                {{t "slr.reimagined.address_selection.error_message"}}
              </Text>
            {{/if}}

            {{! ── Submit Button ── }}
            <button
              type="button"
              class="flex items-center justify-center px-6 py-2 rounded-md font-semibold text-sm leading-none
                {{if
                  this.isSubmitDisabled
                  'bg-gray-300 text-white cursor-not-allowed'
                  'bg-teal-600 text-white cursor-pointer hover:bg-teal-700'
                }}"
              disabled={{this.isSubmitDisabled}}
              data-test-address-selection-submit
              {{on "click" this.handleSubmit.perform}}
            >
              {{t "slr.reimagined.submit"}}
            </button>
          </div>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
